import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { SurveySummary, SurveyComment } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';

const SurveyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [surveys, setSurveys] = useState<SurveySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Action states
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingSurvey, setEditingSurvey] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editExpires, setEditExpires] = useState('');

  // Remark states
  const [remarkSurvey, setRemarkSurvey] = useState<string | null>(null);
  const [remarkText, setRemarkText] = useState('');

  // Comment states
  const [commentSurvey, setCommentSurvey] = useState<string | null>(null);
  const [comments, setComments] = useState<SurveyComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    loadSurveys();
  }, [showArchived]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSurveys(showArchived);
      setSurveys(data);
    } catch (err) {
      console.error('Failed to load surveys:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'draft': return { text: t('survey', 'statusDraft'), color: '#718096', bg: '#edf2f7' };
      case 'active': return { text: t('survey', 'statusActive'), color: '#2c7a7b', bg: '#e6fffa' };
      case 'closed': return { text: t('survey', 'statusClosed'), color: '#9b2c2c', bg: '#fff5f5' };
      default: return { text: status, color: '#718096', bg: '#edf2f7' };
    }
  };

  const handleDelete = async (surveyId: string) => {
    try {
      await apiClient.deleteSurvey(surveyId);
      setSurveys(prev => prev.filter(s => s.surveyId !== surveyId));
      setDeleteConfirm(null);
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const handleEdit = async (surveyId: string) => {
    try {
      await apiClient.updateSurvey(surveyId, { title: editTitle, expiresAt: editExpires ? new Date(editExpires).toISOString() : undefined });
      setEditingSurvey(null);
      loadSurveys();
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const handleArchive = async (surveyId: string, archive: boolean) => {
    try {
      await apiClient.archiveSurvey(surveyId, archive);
      setSurveys(prev => prev.filter(s => s.surveyId !== surveyId));
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const handleRemarkSave = async (surveyId: string) => {
    try {
      await apiClient.updateSurveyRemark(surveyId, remarkText);
      setSurveys(prev => prev.map(s => s.surveyId === surveyId ? { ...s, remark: remarkText } : s));
      setRemarkSurvey(null);
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const openComments = async (surveyId: string) => {
    setCommentSurvey(surveyId);
    setLoadingComments(true);
    try {
      const data = await apiClient.getSurveyComments(surveyId);
      setComments(data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentSurvey || !newComment.trim()) return;
    try {
      const comment = await apiClient.addSurveyComment(commentSurvey, newComment.trim(), 'admin');
      setComments(prev => [...prev, comment]);
      setNewComment('');
      setSurveys(prev => prev.map(s => s.surveyId === commentSurvey ? { ...s, commentCount: s.commentCount + 1 } : s));
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!commentSurvey) return;
    try {
      await apiClient.deleteSurveyComment(commentSurvey, commentId);
      setComments(prev => prev.filter(c => c.commentId !== commentId));
      setSurveys(prev => prev.map(s => s.surveyId === commentSurvey ? { ...s, commentCount: Math.max(0, s.commentCount - 1) } : s));
    } catch (err) {
      alert(t('common', 'deleteFailed'));
    }
  };

  const btnStyle = (bg: string, color: string = 'white'): React.CSSProperties => ({
    padding: '5px 10px',
    backgroundColor: bg,
    color,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500
  });

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>{t('survey', 'management')}</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => navigate('/admin/templates')}
            style={{ padding: '10px 20px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', color: '#4a5568' }}
          >
            {t('survey', 'templateManagement')}
          </button>
          <button
            onClick={() => navigate('/admin/surveys/create')}
            style={{ padding: '10px 20px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem' }}
          >
            {t('survey', 'createNew')}
          </button>
        </div>
      </div>

      {/* Tab: Active / Archived */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        <button
          onClick={() => setShowArchived(false)}
          style={{
            padding: '8px 16px',
            backgroundColor: !showArchived ? '#2c5282' : '#edf2f7',
            color: !showArchived ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: !showArchived ? 600 : 400
          }}
        >
          {t('survey', 'statusActive')}
        </button>
        <button
          onClick={() => setShowArchived(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: showArchived ? '#2c5282' : '#edf2f7',
            color: showArchived ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: showArchived ? 600 : 400
          }}
        >
          {t('survey', 'statusClosed')}
        </button>
      </div>

      {loading ? (
        <p>{t('common', 'loading')}</p>
      ) : surveys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>{t('survey', 'noSurveys')}</p>
          {!showArchived && <p style={{ color: '#a0aec0' }}>{t('survey', 'createPrompt')}</p>}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {surveys.map(s => {
            const status = statusLabel(s.status);
            return (
              <div
                key={s.surveyId}
                style={{
                  backgroundColor: s.hidden ? '#f9fafb' : 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  opacity: s.hidden ? 0.6 : 1,
                  borderLeft: s.hidden ? '3px solid #cbd5e0' : 'none'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div
                    style={{ flex: 1, cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/surveys/${s.surveyId}`)}
                  >
                    <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>
                      {s.title}
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#718096', flexWrap: 'wrap' }}>
                      <span>{t('survey', 'responses')} {s.completedCount}/{s.respondentCount}{t('survey', 'respondentCount')}</span>
                      <span>{t('survey', 'deadline')} {new Date(s.expiresAt).toLocaleDateString('ja-JP')}</span>
                      <span>{t('survey', 'created')} {new Date(s.createdAt).toLocaleDateString('ja-JP')}</span>
                    </div>
                    {s.remark && (
                      <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#805ad5', fontStyle: 'italic' }}>
                        {s.remark}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: status.bg,
                      color: status.color,
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 600
                    }}>
                      {status.text}
                    </span>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'wrap', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingSurvey(s.surveyId); setEditTitle(s.title); setEditExpires(s.expiresAt.split('T')[0]); }}
                    style={btnStyle('#edf2f7', '#2d3748')}
                  >
                    {t('common', 'edit')}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleArchive(s.surveyId, !showArchived); }}
                    style={btnStyle('#ebf8ff', '#2c5282')}
                  >
                    {showArchived ? t('common', 'back') : t('survey', 'statusClosed')}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setRemarkSurvey(s.surveyId); setRemarkText(s.remark || ''); }}
                    style={btnStyle('#faf5ff', '#6b46c1')}
                  >
                    {t('surveyResults', 'remarks')}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openComments(s.surveyId); }}
                    style={btnStyle('#fffff0', '#975a16')}
                  >
                    {s.commentCount > 0 && `(${s.commentCount})`}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(s.surveyId); }}
                    style={btnStyle('#fff5f5', '#c53030')}
                  >
                    {t('common', 'delete')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 16px' }}>{t('common', 'delete')}</h3>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={btnStyle('#edf2f7', '#4a5568')}>{t('common', 'cancel')}</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={btnStyle('#e53e3e')}>{t('common', 'delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {editingSurvey && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ margin: '0 0 16px' }}>{t('common', 'edit')}</h3>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>{t('surveyCreate', 'surveyTitle')}</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: 600 }}>{t('surveyCreate', 'deadline')}</label>
              <input
                type="date"
                value={editExpires}
                onChange={(e) => setEditExpires(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingSurvey(null)} style={btnStyle('#edf2f7', '#4a5568')}>{t('common', 'cancel')}</button>
              <button onClick={() => handleEdit(editingSurvey)} style={btnStyle('#2c5282')}>{t('common', 'save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Remark Dialog */}
      {remarkSurvey && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ margin: '0 0 16px' }}>{t('surveyResults', 'remarks')}</h3>
            <textarea
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              rows={4}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setRemarkSurvey(null)} style={btnStyle('#edf2f7', '#4a5568')}>{t('common', 'cancel')}</button>
              <button onClick={() => handleRemarkSave(remarkSurvey)} style={btnStyle('#6b46c1')}>{t('common', 'save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Dialog */}
      {commentSurvey && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', minHeight: '100px' }}>
              {loadingComments ? (
                <p style={{ color: '#718096' }}>{t('common', 'loading')}</p>
              ) : comments.length === 0 ? (
                <p style={{ color: '#a0aec0', textAlign: 'center' }}></p>
              ) : (
                comments.map(c => (
                  <div key={c.commentId} style={{ padding: '10px', borderBottom: '1px solid #f0f0f0', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '4px' }}>
                          <strong>{c.author}</strong> - {new Date(c.createdAt).toLocaleString('ja-JP')}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#2d3748' }}>{c.text}</div>
                      </div>
                      <button
                        onClick={() => handleDeleteComment(c.commentId)}
                        style={{ background: 'none', border: 'none', color: '#a0aec0', cursor: 'pointer', fontSize: '1rem', padding: '2px 6px' }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.9rem' }}
              />
              <button onClick={handleAddComment} style={btnStyle('#2c5282')} disabled={!newComment.trim()}>{t('common', 'add')}</button>
            </div>
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <button onClick={() => { setCommentSurvey(null); setComments([]); setNewComment(''); }} style={btnStyle('#edf2f7', '#4a5568')}>{t('common', 'close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyDashboard;
